import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { environment } from '../../../environments/environment';
import { TokenDetails } from '../../models/auth/token-details.type';
import { Feature, Profile, Role, Tenant } from '../../models/cube';
import { AuthenticationService } from '../../services/api/authentication/authentication.service';
import { TenantService } from '../../services/api/tenant/tenant.service';
import { ProfileService } from '../../services/api/profile/profile.service';
import { AuthActions } from '../actions/auth.actions';
import { SystemActions } from '../actions/system.actions';
import {
  selectActiveTenant,
  selectTokenDetails,
} from '../selectors/auth.selectors';
import { AppState } from '../state/app.state';

import { Actions, createEffect, ofType } from '@ngrx/effects';import { concatLatestFrom } from '@ngrx/operators';

import { Store } from '@ngrx/store';
import {
  catchError,
  concatMap,
  delay,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { of } from 'rxjs';

@Injectable()
export class AuthEffects {
  private readonly authService: AuthenticationService = inject(
    AuthenticationService
  );

  private readonly actions$: Actions = inject(Actions);
  private readonly profileService: ProfileService = inject(ProfileService);
  private readonly store: Store<AppState> = inject(Store);
  private readonly tenantService: TenantService = inject(TenantService);

  login$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.Login),
        tap(() => this.authService.login())
      );
    },
    { dispatch: false }
  );

  redirectToGateway$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.RedirectToGateway),
        tap(() => this.authService.login())
      );
    },
    { dispatch: false }
  );

  setActiveAccount$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.setActiveAccount),
        tap(() => this.authService.checkAndSetActiveAccount())
      );
    },
    { dispatch: false }
  );

  logout$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.logout),
        tap(() => this.authService.logout())
      );
    },
    { dispatch: false }
  );

  changeActiveTenant$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.setActiveTenant),
      tap(({ active_tenant }) =>
        localStorage.setItem(
          `${environment.localStorageKeyPrefix}_tenant`,
          active_tenant.name
        )
      ),
      concatLatestFrom(() => this.store.select(selectTokenDetails)),
      map(([{ active_tenant, isPageReload }, tokenDetails]) =>
        AuthActions.getUserRoles({
          tokenDetails,
          uniqueId: tokenDetails?.uniqueId ?? '',
          tenant: active_tenant.name,
          isPageReload,
        })
      )
    );
  });

  changeActiveRole$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.setActiveRole),
      concatLatestFrom(() => this.store.select(selectActiveTenant)),
      map(([{ active_role, isPageReload }, tenant]) =>
        AuthActions.getUserFeatures({
          role: active_role.name,
          tenant,
          isPageReload,
        })
      )
    );
  });

  //INFO Api call for get tenant
  userTenants$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getUserTenants),
      switchMap(({ tokenDetails }) =>
        this.authService.tenants(tokenDetails.uniqueId).pipe(
          switchMap((tenants: Array<Tenant>) => {
            if (tenants.length) {
              const current_tenant: string | null = localStorage.getItem(
                `${environment.localStorageKeyPrefix}_tenant`
              );
              const active_tenant: Tenant = current_tenant
                ? { name: current_tenant, info: '' }
                : tenants[0];
              return [
                AuthActions.getUserTenantsSuccess({
                  uniqueId: tokenDetails.uniqueId,
                  tenants,
                }),
                AuthActions.setActiveTenant({
                  active_tenant,
                  isPageReload: false,
                }),
              ];
            } else {
              return [
                AuthActions.setTokenDetails({ tokenDetails: <TokenDetails>{} }),
                SystemActions.RedirectToCourtesyPage(),
              ];
            }
          }),
          catchError((error: HttpErrorResponse) =>
            of(AuthActions.getUserTenantsFailure({ error }))
          )
        )
      )
    );
  });

  userTenantsFailed$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          AuthActions.getUserTenantsFailure,
          AuthActions.getUserRolesFailure
        ),
        tap(({ error }) => {
          if (error.status === HttpStatusCode.NotFound) {
            // eslint-disable-next-line @ngrx/no-dispatch-in-effects
            this.store.dispatch(SystemActions.RedirectToCourtesyPage());
          }
        })
      );
    },
    { dispatch: false }
  );

  //INFO Api call for get roles
  userRoles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getUserRoles),
      delay(200),
      switchMap(({ tokenDetails, uniqueId, tenant, isPageReload }) =>
        this.authService.roles(uniqueId, tenant).pipe(
          map((roles: Array<Role>) => {
            if (roles.length) {
              return AuthActions.getUserRolesSuccess({
                roles,
                tenant,
                isPageReload,
                tokenDetails,
              });
            } else {
              return SystemActions.RedirectToCourtesyPage();
            }
          }),
          catchError((error: HttpErrorResponse) =>
            of(AuthActions.getUserRolesFailure({ error }))
          )
        )
      )
    );
  });

  userRolesSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getUserRolesSuccess),
      map(({ tokenDetails, roles, tenant, isPageReload }) =>
        AuthActions.getUserFeatures({
          tokenDetails,
          role: roles?.[0].name ?? '',
          tenant,
          isPageReload,
        })
      )
    );
  });

  //INFO Api call for get user features
  userFeatures$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getUserFeatures),
      switchMap(({ tokenDetails, role, tenant, isPageReload }) =>
        this.authService.features(role, tenant).pipe(
          concatMap((features: Array<Feature>) => {
            const actions = [];
            if (features.length) {
              // if (isPageReload) {
              //   actions.push(SystemActions.RedirectToHomePage());
              // }
              actions.push(
                AuthActions.getUserFeaturesSuccess({
                  tokenDetails,
                  features,
                  tenant,
                  role,
                  isPageReload,
                })
              );
            } else {
              actions.push(SystemActions.RedirectToCourtesyPage());
            }
            return actions;
          }),
          catchError((error: HttpErrorResponse) =>
            of(AuthActions.getUserFeaturesFailure({ error }))
          )
        )
      )
    );
  });

  //INFO Api call for get tenant details
  userFeaturesSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getUserFeaturesSuccess),
      map(({ tokenDetails, features, tenant, role, isPageReload }) =>
        AuthActions.getTenantDetails({
          tenant,
          tokenDetails,
          role,
          isPageReload,
        })
      )
    );
  });

  userTenantDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getTenantDetails),
      delay(200),
      switchMap(({ tenant, tokenDetails, role, isPageReload }) =>
        this.tenantService.getTenant(tenant).pipe(
          map((tenantDetails) => {
            if (tenantDetails) {
              //   if (isPageReload) {
              //   actions.push(SystemActions.RedirectToHomePage());
              // }
              return AuthActions.setTenantDetails({
                tenantDetails,
                tokenDetails,
                role,
                isPageReload,
              });
            } else {
              return SystemActions.RedirectToCourtesyPage();
            }
          }),
          catchError((error: HttpErrorResponse) =>
            of(AuthActions.getUserRolesFailure({ error }))
          )
        )
      )
    );
  });

  //INFO  Adding user to DB if not present (will be checked in BE)
  createUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.setTenantDetails),
      delay(200),
      switchMap(({ tokenDetails, role, isPageReload }) => {
        if (isPageReload) {
          // Wrap the action in `of()` to convert it to an observable
          return of(SystemActions.RedirectToHomePage());
        }

        // Create a new object based on tokenDetails
        const createUserData: any = {
          company_email: '',
          company: '',
          name: tokenDetails.idTokenClaims.name,
          preferred_language: 'en',
          roles: [role],
          surname: '',
          user_email: tokenDetails.idTokenClaims.email
            ? tokenDetails.idTokenClaims.email
            : tokenDetails.idTokenClaims.verified_email,
        };

        return this.profileService.createUser(createUserData).pipe(
          map((res) => {
            if (res.data) {
              //INFO Load pages after create user api call completed
              return AuthActions.getUserTenantDetailsSuccess({
                pageLoading: true,
              });
            } else {
              return SystemActions.RedirectToCourtesyPage();
            }
          }),
          catchError((error: HttpErrorResponse) =>
            of(AuthActions.getUserRolesFailure({ error }))
          )
        );
      })
    );
  });
}
